import { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AddIcon from '@mui/icons-material/Add'
import { OperatorAccess } from '@numeo/types'
import { SettingCard, Skeleton, Spinner, StatusBadge, Switch, Toast, ToastTone } from '../_ds/Primitives'

interface ToastState {
    open: boolean
    message: string
    tone: ToastTone
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Serialize order-insensitively so Save only enables on a real change.
const serialize = (ops: OperatorAccess[]): string =>
    [...ops]
        .sort((a, b) => a.email.localeCompare(b.email))
        .map((o) => `${o.email}:${o.dispatchHelp ? 1 : 0}:${o.boxTruck ? 1 : 0}:${o.pricing ? 1 : 0}`)
        .join('|')

export default function OperatorsPage() {
    const [operators, setOperators] = useState<OperatorAccess[]>([])
    const [saved, setSaved] = useState<OperatorAccess[]>([])
    const [input, setInput] = useState('')
    const [inputError, setInputError] = useState('')
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [toast, setToast] = useState<ToastState>({ open: false, message: '', tone: 'success' })

    useEffect(() => {
        void fetchOperators()
    }, [])

    const fetchOperators = async (): Promise<void> => {
        try {
            const response = await axios.get<{ success: boolean; data: { operators: OperatorAccess[] } }>('/gluon/config/operators')
            if (response.data.success) {
                setOperators(response.data.data.operators)
                setSaved(response.data.data.operators)
            }
        } catch {
            setToast({ open: true, message: 'Failed to load operators', tone: 'danger' })
        } finally {
            setLoading(false)
        }
    }

    const handleAdd = (): void => {
        const email = input.trim().toLowerCase()
        if (!email) return setInputError('Enter an email address.')
        if (!EMAIL_REGEX.test(email)) return setInputError('Enter a valid email address.')
        if (operators.some((o) => o.email === email)) return setInputError('That email is already on the list.')
        setOperators((prev) => [...prev, { email, dispatchHelp: false, boxTruck: false, pricing: false }])
        setInput('')
        setInputError('')
    }

    const toggle = (email: string, key: 'dispatchHelp' | 'boxTruck' | 'pricing'): void => {
        setOperators((prev) => prev.map((o) => (o.email === email ? { ...o, [key]: !o[key] } : o)))
    }

    const handleRemove = (email: string): void => {
        setOperators((prev) => prev.filter((o) => o.email !== email))
    }

    const handleSave = async (): Promise<void> => {
        setSaving(true)
        try {
            const response = await axios.put<{ success: boolean; data: { operators: OperatorAccess[] } }>('/gluon/config/operators', { operators })
            if (response.data.success) {
                setOperators(response.data.data.operators)
                setSaved(response.data.data.operators)
                setToast({ open: true, message: 'Operators updated', tone: 'success' })
            }
        } catch (error) {
            const message = axios.isAxiosError(error) ? (error.response?.data?.message as string | undefined) : undefined
            setToast({ open: true, message: message || 'Failed to update operators', tone: 'danger' })
        } finally {
            setSaving(false)
        }
    }

    const isDirty = useMemo(() => serialize(operators) !== serialize(saved), [operators, saved])

    return (
        <div className="mx-auto w-full max-w-3xl px-6 py-8">
            <header className="mb-6">
                <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Operators</h1>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                    Grant gated admin surfaces per email. Toggle a feature on to give that account access. Empty or all-off = no access.
                </p>
            </header>

            {loading ? (
                <LoadingStack />
            ) : (
                <SettingCard
                    title="Feature Access"
                    description="Add an email, then enable the surfaces it should see. Access is denied by default."
                    status={
                        <StatusBadge tone={operators.length > 0 ? 'info' : 'neutral'} dot>
                            {operators.length > 0 ? `${operators.length} operator${operators.length === 1 ? '' : 's'}` : 'No operators'}
                        </StatusBadge>
                    }
                    footer={
                        <div className="flex items-center justify-end gap-3">
                            {isDirty && <span className="text-xs text-slate-500 dark:text-slate-400">Unsaved changes</span>}
                            <button
                                type="button"
                                onClick={() => void handleSave()}
                                disabled={saving || !isDirty}
                                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {saving && <Spinner size={14} />}
                                Save
                            </button>
                        </div>
                    }
                >
                    <div className="space-y-5">
                        <div className="flex items-start gap-2">
                            <div className="flex-1">
                                <input
                                    type="email"
                                    value={input}
                                    onChange={(e) => {
                                        setInput(e.target.value)
                                        if (inputError) setInputError('')
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleAdd()
                                    }}
                                    placeholder="operator@example.com"
                                    className={[
                                        'block w-full rounded-lg border bg-white px-3 py-2 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500',
                                        inputError
                                            ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20 dark:border-red-500'
                                            : 'border-slate-300 focus:border-blue-600 focus:ring-blue-600/20 dark:border-slate-600',
                                    ].join(' ')}
                                />
                                {inputError && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{inputError}</p>}
                            </div>
                            <button
                                type="button"
                                onClick={handleAdd}
                                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-900 transition-colors hover:bg-slate-50 active:bg-slate-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                            >
                                <AddIcon fontSize="small" />
                                Add
                            </button>
                        </div>

                        {operators.length === 0 ? (
                            <div className="rounded-lg border border-dashed border-slate-300 px-4 py-8 text-center dark:border-slate-700">
                                <p className="text-sm font-medium text-slate-900 dark:text-slate-100">No operators configured</p>
                                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Add an email to grant feature access.</p>
                            </div>
                        ) : (
                            <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
                                <div className="flex items-center gap-3 border-b border-slate-100 bg-slate-50 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:bg-slate-800/50 dark:text-slate-400">
                                    <span className="flex-1">Email</span>
                                    <span className="w-24 text-center">Dispatch Help</span>
                                    <span className="w-20 text-center">Box Truck</span>
                                    <span className="w-20 text-center">Pricing</span>
                                    <span className="w-8" />
                                </div>
                                <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                                    {operators.map((op) => (
                                        <li key={op.email} className="flex items-center gap-3 px-4 py-2.5">
                                            <span className="min-w-0 flex-1 truncate text-sm text-slate-900 dark:text-slate-100">{op.email}</span>
                                            <span className="flex w-24 justify-center">
                                                <Switch
                                                    checked={!!op.dispatchHelp}
                                                    onChange={() => toggle(op.email, 'dispatchHelp')}
                                                    ariaLabel={`Dispatch Help for ${op.email}`}
                                                />
                                            </span>
                                            <span className="flex w-20 justify-center">
                                                <Switch
                                                    checked={!!op.boxTruck}
                                                    onChange={() => toggle(op.email, 'boxTruck')}
                                                    ariaLabel={`Box Truck for ${op.email}`}
                                                />
                                            </span>
                                            {/* Pricing is its OWN grant. It was gated on the Box Truck tick here, which
                                                forced staff to add a per-email board tick for a dispatcher who already has
                                                the board through their company — a tick that skips the inheriting-role
                                                rules and outlives their membership. It also stranded `pricing: true`
                                                behind a disabled switch the moment Box Truck was unticked. Whether the
                                                panel actually appears is settled at request time by the board guard. */}
                                            <span className="flex w-20 justify-center">
                                                <Switch
                                                    checked={!!op.pricing}
                                                    onChange={() => toggle(op.email, 'pricing')}
                                                    ariaLabel={`Pricing for ${op.email}`}
                                                />
                                            </span>
                                            <button
                                                type="button"
                                                aria-label={`Remove ${op.email}`}
                                                onClick={() => handleRemove(op.email)}
                                                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                                            >
                                                <DeleteOutlineIcon fontSize="small" />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </SettingCard>
            )}

            <Toast open={toast.open} tone={toast.tone} message={toast.message} onClose={() => setToast((prev) => ({ ...prev, open: false }))} />
        </div>
    )
}

// ─── helpers ────────────────────────────────────────────────────────────────

function LoadingStack() {
    return (
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
            <Skeleton className="mb-3 h-5 w-48" />
            <Skeleton className="mb-2 h-3 w-full max-w-lg" />
            <Skeleton className="mb-6 h-3 w-2/3 max-w-lg" />
            <Skeleton className="mb-4 h-9 w-full" />
            <Skeleton className="h-10 w-full" />
        </div>
    )
}
